import { useMemo } from 'react';

function columnIndexToLetter(index) {
  let letter = '';
  let i = index;
  while (i >= 0) {
    letter = String.fromCharCode((i % 26) + 65) + letter;
    i = Math.floor(i / 26) - 1;
  }
  return letter;
}

/**
 * Renders raw spreadsheet data as a read-only grid with row numbers and column letters.
 * Cells outside the selected data range are dimmed; the header row is highlighted.
 *
 * Props:
 * - rows: Array of arrays (raw cell values)
 * - headerRow: 0-indexed row that is the header
 * - dataRange: { startRow, endRow, startCol, endCol } (0-indexed, inclusive)
 * - accentColor: tailwind color prefix for theming (default: 'primary')
 */
export default function RawDataGrid({ rows = [], headerRow = 0, dataRange = {}, accentColor = 'primary' }) {
  const maxCols = useMemo(
    () => Math.max(...rows.map(r => r?.length || 0), 0),
    [rows]
  );

  const { startRow = 0, endRow, startCol = 0, endCol } = dataRange;
  const effectiveEndRow = endRow != null ? endRow : rows.length - 1;
  const effectiveEndCol = endCol != null ? endCol : maxCols - 1;

  const isInRange = (rowIdx, colIdx) =>
    rowIdx >= startRow && rowIdx <= effectiveEndRow &&
    colIdx >= startCol && colIdx <= effectiveEndCol;

  const isHeaderRow = (rowIdx) => rowIdx === headerRow;

  if (rows.length === 0 || maxCols === 0) return null;

  const bgHighlight = accentColor === 'emerald'
    ? 'bg-emerald-100 dark:bg-emerald-900/30'
    : 'bg-primary-100 dark:bg-primary-900/30';
  const textHighlight = accentColor === 'emerald'
    ? 'text-emerald-800 dark:text-emerald-200'
    : 'text-primary-800 dark:text-primary-200';

  return (
    <div className="overflow-auto rounded-lg border border-surface-200 dark:border-surface-700 max-h-80">
      <table className="text-xs border-collapse">
        <thead className="sticky top-0 z-10 bg-surface-100 dark:bg-surface-800">
          <tr>
            {/* Row number header */}
            <th className="px-2 py-1.5 text-center text-surface-400 border-r border-b border-surface-200 dark:border-surface-700 w-10 sticky left-0 bg-surface-100 dark:bg-surface-800 z-20">
              #
            </th>
            {Array.from({ length: maxCols }, (_, i) => {
              const inRange = i >= startCol && i <= effectiveEndCol;
              return (
                <th
                  key={i}
                  className={`px-3 py-1.5 text-center font-medium border-b border-surface-200 dark:border-surface-700 whitespace-nowrap ${
                    inRange
                      ? 'text-surface-600 dark:text-surface-300'
                      : 'text-surface-300 dark:text-surface-600'
                  }`}
                >
                  {columnIndexToLetter(i)}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => {
            const isHeader = isHeaderRow(rowIdx);
            return (
              <tr key={rowIdx}>
                {/* Row number */}
                <td className={`px-2 py-1 text-center font-mono border-r border-surface-200 dark:border-surface-700 sticky left-0 z-10 ${
                  isHeader
                    ? `${bgHighlight} ${textHighlight} font-bold`
                    : isInRange(rowIdx, startCol)
                      ? 'bg-surface-50 dark:bg-surface-850 text-surface-500'
                      : 'bg-surface-50 dark:bg-surface-850 text-surface-300 dark:text-surface-600'
                }`}>
                  {rowIdx + 1}
                </td>
                {Array.from({ length: maxCols }, (_, colIdx) => {
                  const value = row?.[colIdx];
                  const inRange = isInRange(rowIdx, colIdx);
                  const displayValue = value != null ? String(value) : '';

                  let cellClass;
                  if (isHeader && inRange) {
                    cellClass = `${bgHighlight} ${textHighlight} font-semibold`;
                  } else if (inRange) {
                    cellClass = 'text-surface-800 dark:text-surface-200 bg-white dark:bg-surface-900';
                  } else {
                    cellClass = 'text-surface-300 dark:text-surface-600 bg-surface-50/50 dark:bg-surface-900/50';
                  }

                  return (
                    <td
                      key={colIdx}
                      className={`px-3 py-1 whitespace-nowrap max-w-[200px] truncate border-b border-surface-100 dark:border-surface-800 ${cellClass}`}
                      title={displayValue}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
